import { Badge, buttonClass } from "@promptgatewaysadmin/ui";
import { Link } from "react-router-dom";
import { useSession } from "../auth/SessionProvider.js";
import { useSubscriptionState } from "../queries/hooks.js";
import { daysLeft, trialTone, type SubscriptionState } from "../api/normalise.js";

export interface PillSpec {
  text: string;
  tone: "neutral" | "success" | "warn" | "danger";
  /** Admin-only. A member cannot act on a billing CTA, so they never get one. */
  cta: { label: string; to: string } | null;
}

/**
 * The header status slot.
 *
 * Two rules make this whole family of bugs go away:
 *
 *   daysLeft drives PRESENTATION   (pill text, tone, hero copy)
 *   status   drives ACCESS         (what is gated, what is reachable)
 *
 * The day-11/day-14 sweep is server-side and cadence-dependent, so the two can disagree by
 * up to one sweep interval. And never the converse: a converted org has a PAST trialEndsAt
 * and is perfectly `active`, so status is never derived from the date.
 *
 * `active` renders NO pill and no button — a healthy account does not need a status badge.
 */
export function pillFor(
  state: SubscriptionState,
  isAdmin: boolean,
  now = new Date(),
): PillSpec | null {
  const { status } = state;

  if (status === "active" && !state.cancelAtPeriodEnd) return null;

  if (status === "active" && state.cancelAtPeriodEnd) {
    const end = state.currentPeriodEnd
      ? new Intl.DateTimeFormat(undefined, { day: "numeric", month: "short" }).format(
          new Date(state.currentPeriodEnd),
        )
      : "soon";
    return {
      text: `Ends ${end}`,
      tone: "warn",
      cta: isAdmin ? { label: "Resume", to: "/subscription" } : null,
    };
  }

  if (status === "trialing") {
    const days = daysLeft(state.trialEndsAt, now);
    // "reminded" is not a UI status — it is trialing with <= 3 days left, which changes the
    // tone and adds one sentence. No red, no timer, no modal: we sold a calm reminder.
    const text = days === null ? "Trial" : days <= 0 ? "Trial · last day" : `Trial · ${days} day${days === 1 ? "" : "s"} left`;
    return {
      text,
      tone: trialTone(days),
      cta: isAdmin ? { label: "Upgrade", to: "/checkout" } : null,
    };
  }

  if (status === "past_due") {
    return {
      text: "Payment failed",
      tone: "danger",
      cta: isAdmin ? { label: "Update card", to: "/billing" } : null,
    };
  }

  if (status === "locked") {
    return {
      text: "Trial ended",
      tone: "neutral",
      cta: isAdmin ? { label: "Choose a plan", to: "/checkout" } : null,
    };
  }

  if (status === "canceled") {
    return {
      text: "Ended",
      tone: "neutral",
      cta: isAdmin ? { label: "Choose a plan", to: "/checkout" } : null,
    };
  }

  // incomplete — checkout completed, webhook not yet applied. Transient, seconds.
  return { text: "Activating…", tone: "neutral", cta: null };
}

/** Over the seat limit is one of only two conditions that earn a --danger header pill. */
export function seatPill(state: SubscriptionState): PillSpec | null {
  return state.seatCount > state.seatLimit
    ? { text: "Over seat limit", tone: "danger", cta: { label: "Add seats", to: "/team" } }
    : null;
}

export function StatusPill() {
  const session = useSession();
  const { data, isError } = useSubscriptionState();
  const isAdmin = session.role === "admin";

  // NEVER guess a status. If the call failed, the shell renders without the pill rather
  // than showing a cached plan name as if it were current.
  if (isError || !data) return null;

  const seat = seatPill(data);
  const pill = seat ?? pillFor(data, isAdmin);
  if (!pill) return null;

  return (
    <div className="pg-shell__status">
      <Badge tone={pill.tone}>{pill.text}</Badge>
      {pill.cta && (
        <Link className={buttonClass("secondary", "sm")} to={pill.cta.to}>
          {pill.cta.label}
        </Link>
      )}
    </div>
  );
}
